import type { Employee, UserRoleType } from '@omnipresentgroup/types';

import { PERMISSION_MATRICES } from './matrices';
import type {
  BasePermissions,
  PermissionsByResource,
  ResourceType,
} from './types';

export const getPermissionsOnEmployee = <T extends ResourceType>({
  userRoles,
  employee,
  resourceType,
  omnipresentTenantId,
}: {
  userRoles: UserRoleType[];
  employee?: Employee;
  resourceType: T;
  omnipresentTenantId?: string;
}): PermissionsByResource[T] => {
  if (!employee?.managerId || !omnipresentTenantId) {
    const samplePermissions = PERMISSION_MATRICES[resourceType].internal.admin;

    const defaultDeniedPermissions = Object.keys(samplePermissions).reduce(
      (accumulator, key) => ({
        ...accumulator,
        [key]: false,
      }),
      {} as PermissionsByResource[T],
    );

    return defaultDeniedPermissions;
  }

  const isInternalEmployee = employee.managerId === omnipresentTenantId;
  const employeeType = isInternalEmployee ? 'internal' : 'client';

  const getRolePermissions = () =>
    userRoles
      .filter(
        (role): role is UserRoleType =>
          PERMISSION_MATRICES[resourceType][employeeType][role] !== undefined,
      )
      .map((role) => {
        const permissions =
          PERMISSION_MATRICES[resourceType][employeeType][role];
        return Object.keys(permissions).reduce(
          (normalized, key) => ({
            ...normalized,
            [key]: permissions[key] ?? false,
          }),
          {} as BasePermissions & Record<string, boolean>,
        );
      });

  const combinePermissions = (
    permissions: Array<BasePermissions & Record<string, boolean>>,
  ) => {
    const allKeys = new Set(permissions.flatMap((p) => Object.keys(p)));
    return [...allKeys].reduce(
      (combined, key) => ({
        ...combined,
        [key]: permissions.some((p) => p[key]),
      }),
      {} as PermissionsByResource[T],
    );
  };

  return combinePermissions(getRolePermissions());
};
